import React, { useState, useEffect, useCallback } from 'react';
import './SASDashboard.css';

const API = '/api/sas';
const getToken = () => localStorage.getItem('token');
const headers = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` });

const STATUS_LABELS = { trial: 'Testphase', active: 'Aktiv', suspended: 'Gesperrt', cancelled: 'Gekündigt' };
const STATUS_COLORS = { trial: '#f59e0b', active: '#10b981', suspended: '#ef4444', cancelled: '#6b7280' };
const FEE_LABELS = { pauschal: 'Pauschal / Turnier', pro_teilnehmer: 'Pro Teilnehmer', prozent_einnahmen: '% der Einnahmen' };

const ALL_MODULES = [
  { key: 'turniere',         label: 'Turniere' },
  { key: 'vereine',          label: 'Vereine' },
  { key: 'wettkaempfer',     label: 'Wettkämpfer' },
  { key: 'anmeldungen',      label: 'Anmeldungen' },
  { key: 'einwaage',         label: 'Einwaage' },
  { key: 'zeitplan',         label: 'Zeitplan' },
  { key: 'akkreditierung',   label: 'Akkreditierung' },
  { key: 'druck',            label: 'Druck' },
  { key: 'brackets',         label: 'Brackets' },
  { key: 'modus_uebersicht', label: 'Modus-Übersicht' },
  { key: 'formen',           label: 'Formen' },
  { key: 'selbstverteidigung', label: 'Selbstverteidigung' },
  { key: 'matten_wettkampf', label: 'Mattenfläche' },
  { key: 'bruchtest',        label: 'Bruchtest' },
  { key: 'streaming',        label: 'Streaming' },
  { key: 'live_ergebnisse',  label: 'Live-Ergebnisse' },
  { key: 'ergebnisse',       label: 'Ergebnisse' },
  { key: 'siegerehrung',     label: 'Siegerehrung' },
  { key: 'platzierung',      label: 'Platzierung' },
  { key: 'urkunden',         label: 'Urkunden' },
  { key: 'saisonrangliste',  label: 'Saisonrangliste' },
  { key: 'analytics',        label: 'Analytics' },
  { key: 'berichte',         label: 'Berichte' },
];

// ── Helpers ──────────────────────────────────────────────────
function fmt(n) { return Number(n || 0).toFixed(2).replace('.', ',') + ' €'; }

// ── Leerer Tenant / Paket ────────────────────────────────────
const emptyTenant = () => ({
  name: '', subdomain: '', type: 'verein', status: 'trial', package_id: '',
  grundgebuehr: '', turnier_fee_type: 'pauschal', turnier_fee_value: '',
  kontakt_name: '', kontakt_email: '', kontakt_telefon: '', notizen: '', trial_until: ''
});
const emptyFeatures = () => Object.fromEntries(ALL_MODULES.map(m => [m.key, false]));
const emptyPackage = () => ({
  name: '', description: '',
  features: emptyFeatures(),
  default_grundgebuehr: '', default_turnier_fee_type: 'pauschal',
  default_turnier_fee_value: '', sort_order: 0
});

// ════════════════════════════════════════════════════════════
export default function SASDashboard() {
  const [view, setView] = useState('overview'); // overview | tenants | packages | invoices
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [packages, setPackages] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modals
  const [tenantModal, setTenantModal] = useState(null);   // null | { mode:'create'|'edit', data }
  const [packageModal, setPackageModal] = useState(null); // null | { mode:'create'|'edit', data }
  const [tenantDetail, setTenantDetail] = useState(null); // null | tenant-Objekt
  const [invoiceModal, setInvoiceModal] = useState(null); // null | { tenant_id, tenant_name }

  const [msg, setMsg] = useState(null);

  const flash = (text, type = 'ok') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3500);
  };

  // ── Laden ─────────────────────────────────────────────────
  const loadStats = useCallback(async () => {
    const r = await fetch(`${API}/stats`, { headers: headers() });
    const d = await r.json();
    if (d.success) setStats(d.data);
  }, []);

  const loadTenants = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/tenants`, { headers: headers() });
    const d = await r.json();
    if (d.success) setTenants(d.data);
    setLoading(false);
  }, []);

  const loadPackages = useCallback(async () => {
    const r = await fetch(`${API}/packages`, { headers: headers() });
    const d = await r.json();
    if (d.success) setPackages(d.data);
  }, []);

  const loadInvoices = useCallback(async (tenantId) => {
    const url = tenantId ? `${API}/invoices?tenant_id=${tenantId}` : `${API}/invoices`;
    const r = await fetch(url, { headers: headers() });
    const d = await r.json();
    if (d.success) setInvoices(d.data);
  }, []);

  useEffect(() => {
    loadStats();
    loadPackages();
  }, [loadStats, loadPackages]);

  useEffect(() => {
    if (view === 'tenants') loadTenants();
    if (view === 'packages') loadPackages();
    if (view === 'invoices') loadInvoices();
    if (view === 'overview') { loadStats(); loadTenants(); }
  }, [view, loadStats, loadTenants, loadPackages, loadInvoices]);

  // ── Tenant speichern ──────────────────────────────────────
  const saveTenant = async (formData) => {
    const isEdit = tenantModal.mode === 'edit';
    const url = isEdit ? `${API}/tenants/${tenantModal.data.id}` : `${API}/tenants`;
    const method = isEdit ? 'PUT' : 'POST';
    const r = await fetch(url, { method, headers: headers(), body: JSON.stringify(formData) });
    const d = await r.json();
    if (d.success) {
      flash(isEdit ? 'Gespeichert' : 'Tenant angelegt');
      setTenantModal(null);
      loadTenants(); loadStats();
    } else {
      flash(d.error || 'Fehler', 'err');
    }
  };

  const deleteTenant = async (id, name) => {
    if (!window.confirm(`"${name}" wirklich löschen?`)) return;
    const r = await fetch(`${API}/tenants/${id}`, { method: 'DELETE', headers: headers() });
    const d = await r.json();
    if (d.success) { flash('Gelöscht'); loadTenants(); loadStats(); }
    else flash(d.error || 'Fehler', 'err');
  };

  // ── Paket speichern ───────────────────────────────────────
  const savePackage = async (formData) => {
    const isEdit = packageModal.mode === 'edit';
    const url = isEdit ? `${API}/packages/${packageModal.data.id}` : `${API}/packages`;
    const method = isEdit ? 'PUT' : 'POST';
    const r = await fetch(url, { method, headers: headers(), body: JSON.stringify(formData) });
    const d = await r.json();
    if (d.success) {
      flash(isEdit ? 'Paket gespeichert' : 'Paket angelegt');
      setPackageModal(null);
      loadPackages();
    } else flash(d.error || 'Fehler', 'err');
  };

  const deletePackage = async (id, name) => {
    if (!window.confirm(`Paket "${name}" löschen?`)) return;
    const r = await fetch(`${API}/packages/${id}`, { method: 'DELETE', headers: headers() });
    const d = await r.json();
    if (d.success) { flash('Paket gelöscht'); loadPackages(); }
    else flash(d.error || 'Fehler', 'err');
  };

  // ── Rechnung Status ───────────────────────────────────────
  const setInvoiceStatus = async (id, status) => {
    const r = await fetch(`${API}/invoices/${id}/status`, { method: 'PUT', headers: headers(), body: JSON.stringify({ status }) });
    const d = await r.json();
    if (d.success) { flash('Status aktualisiert'); loadInvoices(); }
    else flash(d.error || 'Fehler', 'err');
  };

  // ── Neue Rechnung ─────────────────────────────────────────
  const createInvoice = async (formData) => {
    const r = await fetch(`${API}/invoices`, { method: 'POST', headers: headers(), body: JSON.stringify(formData) });
    const d = await r.json();
    if (d.success) { flash('Rechnung erstellt'); setInvoiceModal(null); loadInvoices(); }
    else flash(d.error || 'Fehler', 'err');
  };

  const NAV_CARDS = [
    { key: 'overview',  kanji: '概要', title: 'Übersicht',   sub: 'Statistiken & Schnellzugriff' },
    { key: 'tenants',   kanji: '顧客', title: 'Kunden',      sub: 'Tenants verwalten' },
    { key: 'packages',  kanji: '商品', title: 'Pakete',      sub: 'Lizenzpakete & Module' },
    { key: 'invoices',  kanji: '請求', title: 'Rechnungen',  sub: 'Abrechnung & Zahlungen' },
  ];

  return (
    <div className="sas-dashboard">
      {/* Flash */}
      {msg && <div className={`sas-flash sas-flash--${msg.type}`}>{msg.text}</div>}

      {/* Navigation als Module-Cards */}
      <div className="modules-grid">
        {NAV_CARDS.map(({ key, kanji, title, sub }) => (
          <div
            key={key}
            className={`module-card${view === key ? ' module-card--active' : ''}`}
            onClick={() => setView(key)}
          >
            <div className="module-icon">{kanji}</div>
            <div className="module-content">
              <h4>{title}</h4>
              <p>{sub}</p>
            </div>
            <div className="module-arrow">→</div>
          </div>
        ))}
      </div>

      {/* ── ÜBERSICHT ─────────────────────────────────────── */}
      {view === 'overview' && stats && (
        <div className="sas-overview">
          <div className="sas-stats-row">
            <StatCard label="Tenants gesamt" value={stats.tenants.gesamt} color="#d4af37" kanji="総数" />
            <StatCard label="Aktiv" value={stats.tenants.aktiv} color="#10b981" kanji="有効" />
            <StatCard label="Testphase" value={stats.tenants.trial} color="#f59e0b" kanji="試用" />
            <StatCard label="Gesperrt" value={stats.tenants.gesperrt} color="#ef4444" kanji="停止" />
            <StatCard label="Offen (€)" value={fmt(stats.umsatz.gesamt_offen)} color="#94a3b8" kanji="未払" isText />
            <StatCard label="Bezahlt (€)" value={fmt(stats.umsatz.gesamt_bezahlt)} color="#10b981" kanji="済" isText />
          </div>

          <div className="sas-section-title">Letzte Tenants</div>
          <TenantTable
            tenants={tenants.slice(0, 8)}
            packages={packages}
            onEdit={t => setTenantModal({ mode: 'edit', data: t })}
            onDelete={deleteTenant}
            onInvoice={t => { setInvoiceModal({ tenant_id: t.id, tenant_name: t.name }); setView('invoices'); loadInvoices(); }}
          />

          <button className="sas-btn sas-btn--primary" style={{ marginTop: '1rem' }}
            onClick={() => { setTenantModal({ mode: 'create', data: emptyTenant() }); }}>
            + Neuen Tenant anlegen
          </button>
        </div>
      )}

      {/* ── TENANTS ───────────────────────────────────────── */}
      {view === 'tenants' && (
        <div className="sas-section">
          <div className="sas-section-toolbar">
            <div className="sas-section-title">Alle Tenants</div>
            <button className="sas-btn sas-btn--primary"
              onClick={() => setTenantModal({ mode: 'create', data: emptyTenant() })}>
              + Neuer Tenant
            </button>
          </div>
          {loading ? <div className="sas-loading">Laden…</div> : (
            <TenantTable
              tenants={tenants}
              packages={packages}
              onEdit={t => setTenantModal({ mode: 'edit', data: t })}
              onDelete={deleteTenant}
              onInvoice={t => { setInvoiceModal({ tenant_id: t.id, tenant_name: t.name }); setView('invoices'); loadInvoices(); }}
            />
          )}
        </div>
      )}

      {/* ── PAKETE ────────────────────────────────────────── */}
      {view === 'packages' && (
        <div className="sas-section">
          <div className="sas-section-toolbar">
            <div className="sas-section-title">Pakete</div>
            <button className="sas-btn sas-btn--primary"
              onClick={() => setPackageModal({ mode: 'create', data: emptyPackage() })}>
              + Neues Paket
            </button>
          </div>
          <div className="sas-packages-grid">
            {packages.map(pkg => (
              <PackageCard key={pkg.id} pkg={pkg}
                onEdit={() => setPackageModal({ mode: 'edit', data: {
                  ...pkg,
                  features: typeof pkg.features === 'string' ? JSON.parse(pkg.features) : pkg.features
                }})}
                onDelete={() => deletePackage(pkg.id, pkg.name)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── RECHNUNGEN ────────────────────────────────────── */}
      {view === 'invoices' && (
        <div className="sas-section">
          <div className="sas-section-toolbar">
            <div className="sas-section-title">Rechnungen</div>
            <button className="sas-btn sas-btn--primary"
              onClick={() => setInvoiceModal({ tenant_id: '', tenant_name: '' })}>
              + Neue Rechnung
            </button>
          </div>
          <table className="sas-table">
            <thead>
              <tr>
                <th>Tenant</th><th>Periode</th><th>Grundgebühr</th>
                <th>Turnier-Betrag</th><th>Gesamt</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id}>
                  <td>{inv.tenant_name}</td>
                  <td>{inv.periode}</td>
                  <td>{fmt(inv.grundgebuehr)}</td>
                  <td>{fmt(inv.turnier_betrag)}</td>
                  <td><strong>{fmt(inv.gesamt)}</strong></td>
                  <td>
                    <span className="sas-badge" style={{ background: inv.status === 'bezahlt' ? '#10b981' : inv.status === 'storniert' ? '#6b7280' : '#f59e0b' }}>
                      {inv.status}
                    </span>
                  </td>
                  <td>
                    {inv.status === 'offen' && (
                      <button className="sas-btn sas-btn--sm sas-btn--green" onClick={() => setInvoiceStatus(inv.id, 'bezahlt')}>Bezahlt</button>
                    )}
                    {inv.status === 'offen' && (
                      <button className="sas-btn sas-btn--sm sas-btn--muted" style={{ marginLeft: 4 }} onClick={() => setInvoiceStatus(inv.id, 'storniert')}>Storno</button>
                    )}
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', opacity: 0.5 }}>Keine Rechnungen</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ── MODALS ────────────────────────────────────────── */}
      {tenantModal && (
        <TenantModal
          mode={tenantModal.mode}
          data={tenantModal.data}
          packages={packages}
          onSave={saveTenant}
          onClose={() => setTenantModal(null)}
        />
      )}
      {packageModal && (
        <PackageModal
          mode={packageModal.mode}
          data={packageModal.data}
          onSave={savePackage}
          onClose={() => setPackageModal(null)}
        />
      )}
      {invoiceModal && (
        <InvoiceModal
          tenants={tenants}
          initial={invoiceModal}
          onSave={createInvoice}
          onClose={() => setInvoiceModal(null)}
        />
      )}
    </div>
  );
}

// ── StatCard ──────────────────────────────────────────────────
function StatCard({ label, value, color, kanji, isText }) {
  return (
    <div className="sas-stat-card">
      <div className="sas-stat-kanji" style={{ color }}>{kanji}</div>
      <div className="sas-stat-value" style={{ color: isText ? color : '#fff' }}>{value}</div>
      <div className="sas-stat-label">{label}</div>
    </div>
  );
}

// ── TenantTable ───────────────────────────────────────────────
function TenantTable({ tenants, packages, onEdit, onDelete, onInvoice }) {
  return (
    <table className="sas-table">
      <thead>
        <tr>
          <th>Name</th><th>Subdomain</th><th>Typ</th><th>Paket</th>
          <th>Grundgebühr</th><th>Turnier-Fee</th><th>Status</th><th></th>
        </tr>
      </thead>
      <tbody>
        {tenants.map(t => {
          const pkg = packages.find(p => p.id === t.package_id);
          return (
            <tr key={t.id}>
              <td><strong>{t.name}</strong></td>
              <td><code className="sas-subdomain">{t.subdomain}.events.tda-intl.org</code></td>
              <td>{t.type === 'verband' ? 'Verband' : 'Verein'}</td>
              <td>{pkg?.name || <span style={{ opacity: 0.4 }}>—</span>}</td>
              <td>{fmt(t.grundgebuehr)}</td>
              <td>
                {t.turnier_fee_type === 'pauschal' && `${fmt(t.turnier_fee_value)} flat`}
                {t.turnier_fee_type === 'pro_teilnehmer' && `${fmt(t.turnier_fee_value)} / TN`}
                {t.turnier_fee_type === 'prozent_einnahmen' && `${Number(t.turnier_fee_value || 0).toFixed(1)}%`}
              </td>
              <td>
                <span className="sas-badge" style={{ background: STATUS_COLORS[t.status] }}>
                  {STATUS_LABELS[t.status]}
                </span>
              </td>
              <td className="sas-actions">
                <button className="sas-btn sas-btn--sm" onClick={() => onEdit(t)}>Bearbeiten</button>
                <button className="sas-btn sas-btn--sm sas-btn--gold" onClick={() => onInvoice(t)}>Rechnung</button>
                <button className="sas-btn sas-btn--sm sas-btn--danger" onClick={() => onDelete(t.id, t.name)}>✕</button>
              </td>
            </tr>
          );
        })}
        {tenants.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', opacity: 0.5 }}>Keine Tenants</td></tr>}
      </tbody>
    </table>
  );
}

// ── PackageCard ───────────────────────────────────────────────
function PackageCard({ pkg, onEdit, onDelete }) {
  const features = typeof pkg.features === 'string' ? JSON.parse(pkg.features) : pkg.features || {};
  const activeCount = Object.values(features).filter(Boolean).length;
  return (
    <div className="sas-package-card">
      <div className="sas-package-header">
        <div>
          <div className="sas-package-name">{pkg.name}</div>
          <div className="sas-package-desc">{pkg.description}</div>
        </div>
        <div className="sas-package-actions">
          <button className="sas-btn sas-btn--sm" onClick={onEdit}>Bearbeiten</button>
          <button className="sas-btn sas-btn--sm sas-btn--danger" onClick={onDelete}>✕</button>
        </div>
      </div>
      <div className="sas-package-pricing">
        <span>Grundgebühr: <strong>{fmt(pkg.default_grundgebuehr)}</strong></span>
        <span>{FEE_LABELS[pkg.default_turnier_fee_type]}: <strong>
          {pkg.default_turnier_fee_type === 'prozent_einnahmen'
            ? `${Number(pkg.default_turnier_fee_value || 0).toFixed(1)}%`
            : fmt(pkg.default_turnier_fee_value)}
        </strong></span>
      </div>
      <div className="sas-module-grid">
        {ALL_MODULES.map(m => (
          <div key={m.key} className={`sas-module-chip${features[m.key] ? ' active' : ''}`}>
            {m.label}
          </div>
        ))}
      </div>
      <div className="sas-package-footer">{activeCount} / {ALL_MODULES.length} Module aktiv</div>
    </div>
  );
}

// ── TenantModal ───────────────────────────────────────────────
function TenantModal({ mode, data, packages, onSave, onClose }) {
  const [form, setForm] = useState({ ...data });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="sas-overlay" onClick={onClose}>
      <div className="sas-modal" onClick={e => e.stopPropagation()}>
        <div className="sas-modal-header">
          <h3>{mode === 'create' ? 'Neuen Tenant anlegen' : `Tenant bearbeiten: ${data.name}`}</h3>
          <button className="sas-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="sas-modal-body">
          <div className="sas-form-grid">
            <label>Name *<input value={form.name} onChange={e => set('name', e.target.value)} /></label>
            {mode === 'create' && (
              <label>Subdomain *
                <div className="sas-subdomain-input">
                  <input value={form.subdomain} onChange={e => set('subdomain', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} placeholder="mein-verein" />
                  <span>.events.tda-intl.org</span>
                </div>
              </label>
            )}
            <label>Typ
              <select value={form.type} onChange={e => set('type', e.target.value)}>
                <option value="verein">Verein</option>
                <option value="verband">Verband</option>
              </select>
            </label>
            <label>Status
              <select value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="trial">Testphase</option>
                <option value="active">Aktiv</option>
                <option value="suspended">Gesperrt</option>
                <option value="cancelled">Gekündigt</option>
              </select>
            </label>
            <label>Paket
              <select value={form.package_id || ''} onChange={e => {
                const pkg = packages.find(p => p.id === parseInt(e.target.value));
                set('package_id', e.target.value);
                if (pkg) {
                  set('grundgebuehr', pkg.default_grundgebuehr);
                  set('turnier_fee_type', pkg.default_turnier_fee_type);
                  set('turnier_fee_value', pkg.default_turnier_fee_value);
                }
              }}>
                <option value="">— kein Paket —</option>
                {packages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label>Trial bis
              <input type="date" value={form.trial_until || ''} onChange={e => set('trial_until', e.target.value)} />
            </label>
          </div>

          <div className="sas-modal-section">Preisgestaltung</div>
          <div className="sas-form-grid">
            <label>Grundgebühr (€/Mon)
              <input type="number" step="0.01" value={form.grundgebuehr} onChange={e => set('grundgebuehr', e.target.value)} />
            </label>
            <label>Turnier-Abrechnung
              <select value={form.turnier_fee_type} onChange={e => set('turnier_fee_type', e.target.value)}>
                <option value="pauschal">Pauschal / Turnier</option>
                <option value="pro_teilnehmer">Pro Teilnehmer</option>
                <option value="prozent_einnahmen">% der Einnahmen</option>
              </select>
            </label>
            <label>
              {form.turnier_fee_type === 'prozent_einnahmen' ? 'Prozentsatz (%)' : 'Betrag (€)'}
              <input type="number" step="0.01" value={form.turnier_fee_value} onChange={e => set('turnier_fee_value', e.target.value)} />
            </label>
          </div>

          <div className="sas-modal-section">Kontakt</div>
          <div className="sas-form-grid">
            <label>Ansprechpartner<input value={form.kontakt_name || ''} onChange={e => set('kontakt_name', e.target.value)} /></label>
            <label>E-Mail<input type="email" value={form.kontakt_email || ''} onChange={e => set('kontakt_email', e.target.value)} /></label>
            <label>Telefon<input value={form.kontakt_telefon || ''} onChange={e => set('kontakt_telefon', e.target.value)} /></label>
          </div>
          <label style={{ display: 'block', marginTop: '0.75rem' }}>Notizen
            <textarea rows={3} value={form.notizen || ''} onChange={e => set('notizen', e.target.value)} style={{ width: '100%', resize: 'vertical' }} />
          </label>
        </div>
        <div className="sas-modal-footer">
          <button className="sas-btn sas-btn--muted" onClick={onClose}>Abbrechen</button>
          <button className="sas-btn sas-btn--primary" onClick={() => onSave(form)}>
            {mode === 'create' ? 'Anlegen' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PackageModal ──────────────────────────────────────────────
function PackageModal({ mode, data, onSave, onClose }) {
  const [form, setForm] = useState({ ...data, features: { ...data.features } });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleFeature = (key) => setForm(f => ({ ...f, features: { ...f.features, [key]: !f.features[key] } }));
  const allOn = () => setForm(f => ({ ...f, features: Object.fromEntries(ALL_MODULES.map(m => [m.key, true])) }));
  const allOff = () => setForm(f => ({ ...f, features: Object.fromEntries(ALL_MODULES.map(m => [m.key, false])) }));

  return (
    <div className="sas-overlay" onClick={onClose}>
      <div className="sas-modal sas-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="sas-modal-header">
          <h3>{mode === 'create' ? 'Neues Paket' : `Paket bearbeiten: ${data.name}`}</h3>
          <button className="sas-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="sas-modal-body">
          <div className="sas-form-grid">
            <label>Name *<input value={form.name} onChange={e => set('name', e.target.value)} /></label>
            <label>Sortierung<input type="number" value={form.sort_order} onChange={e => set('sort_order', e.target.value)} /></label>
            <label style={{ gridColumn: '1/-1' }}>Beschreibung<input value={form.description || ''} onChange={e => set('description', e.target.value)} /></label>
          </div>

          <div className="sas-modal-section">Standard-Preise</div>
          <div className="sas-form-grid">
            <label>Grundgebühr (€/Mon)
              <input type="number" step="0.01" value={form.default_grundgebuehr} onChange={e => set('default_grundgebuehr', e.target.value)} />
            </label>
            <label>Turnier-Abrechnung
              <select value={form.default_turnier_fee_type} onChange={e => set('default_turnier_fee_type', e.target.value)}>
                <option value="pauschal">Pauschal / Turnier</option>
                <option value="pro_teilnehmer">Pro Teilnehmer</option>
                <option value="prozent_einnahmen">% der Einnahmen</option>
              </select>
            </label>
            <label>
              {form.default_turnier_fee_type === 'prozent_einnahmen' ? 'Prozentsatz (%)' : 'Betrag (€)'}
              <input type="number" step="0.01" value={form.default_turnier_fee_value} onChange={e => set('default_turnier_fee_value', e.target.value)} />
            </label>
          </div>

          <div className="sas-modal-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Module</span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="sas-btn sas-btn--sm" onClick={allOn}>Alle an</button>
              <button className="sas-btn sas-btn--sm sas-btn--muted" onClick={allOff}>Alle aus</button>
            </div>
          </div>
          <div className="sas-module-toggle-grid">
            {ALL_MODULES.map(m => (
              <label key={m.key} className={`sas-module-toggle${form.features[m.key] ? ' active' : ''}`} onClick={() => toggleFeature(m.key)}>
                <span className="sas-toggle-dot" />
                {m.label}
              </label>
            ))}
          </div>
        </div>
        <div className="sas-modal-footer">
          <button className="sas-btn sas-btn--muted" onClick={onClose}>Abbrechen</button>
          <button className="sas-btn sas-btn--primary" onClick={() => onSave(form)}>Speichern</button>
        </div>
      </div>
    </div>
  );
}

// ── InvoiceModal ──────────────────────────────────────────────
function InvoiceModal({ tenants, initial, onSave, onClose }) {
  const now = new Date();
  const [form, setForm] = useState({
    tenant_id: initial.tenant_id || '',
    periode: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
    grundgebuehr: '', turnier_anzahl: '', turnier_teilnehmer: '',
    turnier_einnahmen: '', turnier_betrag: '', gesamt: '', notizen: ''
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Auto-Gesamt berechnen
  useEffect(() => {
    const g = parseFloat(form.grundgebuehr) || 0;
    const t = parseFloat(form.turnier_betrag) || 0;
    setForm(f => ({ ...f, gesamt: (g + t).toFixed(2) }));
  }, [form.grundgebuehr, form.turnier_betrag]);

  return (
    <div className="sas-overlay" onClick={onClose}>
      <div className="sas-modal" onClick={e => e.stopPropagation()}>
        <div className="sas-modal-header">
          <h3>Neue Rechnung{initial.tenant_name ? ` — ${initial.tenant_name}` : ''}</h3>
          <button className="sas-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="sas-modal-body">
          <div className="sas-form-grid">
            <label>Tenant *
              <select value={form.tenant_id} onChange={e => set('tenant_id', e.target.value)}>
                <option value="">— wählen —</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <label>Periode (JJJJ-MM) *
              <input value={form.periode} onChange={e => set('periode', e.target.value)} placeholder="2026-04" />
            </label>
            <label>Grundgebühr (€)
              <input type="number" step="0.01" value={form.grundgebuehr} onChange={e => set('grundgebuehr', e.target.value)} />
            </label>
            <label>Anzahl Turniere
              <input type="number" value={form.turnier_anzahl} onChange={e => set('turnier_anzahl', e.target.value)} />
            </label>
            <label>Teilnehmer gesamt
              <input type="number" value={form.turnier_teilnehmer} onChange={e => set('turnier_teilnehmer', e.target.value)} />
            </label>
            <label>Einnahmen Turniere (€)
              <input type="number" step="0.01" value={form.turnier_einnahmen} onChange={e => set('turnier_einnahmen', e.target.value)} />
            </label>
            <label>Turnier-Betrag (€)
              <input type="number" step="0.01" value={form.turnier_betrag} onChange={e => set('turnier_betrag', e.target.value)} />
            </label>
            <label>Gesamt (€)
              <input type="number" step="0.01" value={form.gesamt} onChange={e => set('gesamt', e.target.value)} style={{ fontWeight: 700 }} />
            </label>
          </div>
          <label style={{ display: 'block', marginTop: '0.75rem' }}>Notizen
            <textarea rows={2} value={form.notizen} onChange={e => set('notizen', e.target.value)} style={{ width: '100%', resize: 'vertical' }} />
          </label>
        </div>
        <div className="sas-modal-footer">
          <button className="sas-btn sas-btn--muted" onClick={onClose}>Abbrechen</button>
          <button className="sas-btn sas-btn--primary" onClick={() => onSave(form)}>Erstellen</button>
        </div>
      </div>
    </div>
  );
}
